<template>
  <label class="reconciliation-lead" :class="{ 'reconciliation-lead--selected': selected }">
    <input
      type="radio"
      name="reconciliation-lead"
      :value="lead.id"
      :checked="selected"
      @change="$emit('select', lead)"
    >
    <span class="reconciliation-lead__content">
      <span class="reconciliation-lead__header">
        <span
          v-if="countryCode"
          :class="`fflag ff-sm fflag-${countryCode} reconciliation-lead__country`"
        ></span>
        <strong>{{ lead.name }}</strong>
        <span
          v-tooltip="{ content: managerTooltip, placement: 'top-start', offset: 8, classes: 'tooltip--new' }"
          class="reconciliation-lead__manager"
        >
          <img v-if="managerAvatar" :src="managerAvatar" alt="">
          <span v-else>{{ managerInitials }}</span>
        </span>
        <AppStatusBadge
          v-if="leadState"
          design="new"
          compact
          class="reconciliation-lead__status"
          :color="leadState.color || ''"
          :label="leadState.name"
        />
        <router-link
          v-tooltip="{ content: 'Open lead', placement: 'left', offset: 8, classes: 'tooltip--new' }"
          :to="{ name: 'leads-detail-lead', params: { id: lead.id } }"
          class="reconciliation-lead__open"
          aria-label="Open lead in a new tab"
          target="_blank"
          @click.native.stop
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M9 2h5v5M14 2 7.5 8.5M12.5 9.5v3a1.5 1.5 0 0 1-1.5 1.5H3.5A1.5 1.5 0 0 1 2 12.5V5a1.5 1.5 0 0 1 1.5-1.5h3" />
          </svg>
        </router-link>
      </span>

    </span>
  </label>
</template>

<script>
export default {
  name: 'CommunicationReconciliationLeadCard',
  props: {
    lead: {
      type: Object,
      required: true
    },
    selected: Boolean,
    flowVersion: {
      type: String,
      required: true
    },
    relativeDate: {
      type: Function,
      required: true
    }
  },
  computed: {
    manager() {
      return this.lead.manager || null
    },
    managerName() {
      if (!this.manager) return 'Unassigned'
      return this.manager.name || this.manager.full_name || 'Unassigned'
    },
    managerAvatar() {
      if (!this.manager) return ''
      return this.manager.avatar_url
        || this.manager.avatar
        || this.manager.photo_url
        || this.manager.photo
        || ''
    },
    managerInitials() {
      if (!this.manager) return '—'
      return this.managerName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('') || '—'
    },
    managerTooltip() {
      const details = []
      if (this.manager) details.push(this.managerName)
      if (this.lead.created_at) details.push(`Created ${ this.relativeDate(this.lead.created_at) }`)
      return details.join(' · ') || 'Unassigned'
    },
    countryCode() {
      const code = this.lead.country && this.lead.country.code ? String(this.lead.country.code) : ''
      return /^[a-z]{2}$/i.test(code) ? code : ''
    },
    leadState() {
      if (this.flowVersion === 'v2') return this.lead.stage || { name: 'Stage not set' }
      return this.lead.status
    }
  }
}
</script>

<style lang="scss" scoped>
.reconciliation-lead {
  display: flex;
  min-width: 0;
  padding: $ui-space-12;
  border: 1px solid $ui-line-strong;
  border-radius: $ui-panel-radius;
  background-color: $ui-surface;
  color: $ui-text;
  cursor: pointer;
  font-family: $ui-font-body;
  gap: $ui-space-8;
  transition: border-color .15s ease, background-color .15s ease;

  &:hover {
    border-color: $ui-blue;
  }

  &:focus-within {
    outline: 2px solid $ui-blue;
    outline-offset: 2px;
  }

  &--selected {
    border-color: $ui-blue;
    background-color: $ui-blue-soft;
  }

  > input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    accent-color: $ui-blue;
  }

  &__content {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
  }

  &__header {
    display: flex;
    min-width: 0;
    min-height: 36px;
    align-items: center;
    gap: $ui-space-8;

    > strong {
      min-width: 0;
      overflow: hidden;
      color: $ui-ink;
      flex: 0 1 auto;
      font-size: $ui-font-size-body;
      font-weight: 600;
      line-height: 1.35;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__manager {
    display: inline-flex;
    width: 28px;
    height: 28px;
    flex: 0 0 28px;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-radius: 50%;
    background-color: $ui-blue-soft;
    color: $ui-blue;
    font-size: $ui-font-size-metadata;
    font-weight: 600;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__country {
    flex: 0 0 auto;
  }

  &__status {
    max-width: 150px;
    margin-left: auto;
    overflow: hidden;
    flex: 0 1 auto;
    text-overflow: ellipsis;
  }

  &__open {
    display: inline-flex;
    width: $ui-control-height-compact;
    height: $ui-control-height-compact;
    flex: 0 0 $ui-control-height-compact;
    align-items: center;
    justify-content: center;
    border: 1px solid $ui-line-strong;
    border-radius: $ui-control-radius;
    background-color: $ui-surface;
    color: $ui-blue;
    transition: background-color .15s ease, border-color .15s ease;

    &:hover {
      border-color: $ui-blue;
      background-color: $ui-blue-soft;
    }

    &:focus-visible {
      outline: 2px solid $ui-blue;
      outline-offset: 2px;
    }

    svg {
      width: 16px;
      height: 16px;
      fill: none;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.75;
    }
  }
}
</style>
